import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, CheckCircle, Circle, Clock, Play, Pause, BookOpen, Video, Music, FileText, Link, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseViewer = ({ course, user, onBack }) => {
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const currentLesson = course.lessons?.[currentLessonIndex];

  useEffect(() => {
    fetchProgress();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        saveProgress(false);
      }
    };
  }, []);

  useEffect(() => {
    // Start timer when viewing a lesson
    if (currentLesson) {
      startTimeRef.current = Date.now();
      setTimeSpent(0);
      setIsPlaying(true);
      
      timerRef.current = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentLessonIndex]);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/courses/${course.id}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.id) {
        setProgress(response.data);
        // Resume from last lesson
        if (response.data.last_lesson_id) {
          const lastIndex = course.lessons?.findIndex(l => l.id === response.data.last_lesson_id);
          if (lastIndex !== -1 && lastIndex < course.lessons.length - 1) {
            setCurrentLessonIndex(lastIndex);
          }
        }
        // Check if already completed
        if (response.data.completed_at) {
          setCourseCompleted(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress');
    }
  };

  const saveProgress = async (completed = false) => {
    if (!currentLesson) return;

    const minutesSpent = Math.ceil(timeSpent / 60);
    if (minutesSpent === 0 && !completed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/courses/${course.id}/progress`, {
        lesson_id: currentLesson.id,
        time_spent_minutes: minutesSpent,
        completed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (completed) {
        setProgress(prev => ({
          ...prev,
          completed_lessons: [...(prev?.completed_lessons || []), currentLesson.id]
        }));
        toast.success('Lesson completed! Progress saved.');
      }
    } catch (error) {
      console.error('Failed to save progress');
    }
  };

  const handleMarkComplete = async () => {
    await saveProgress(true);
    
    // Check if all lessons are now completed
    const completedCount = (progress?.completed_lessons?.length || 0) + 1;
    if (completedCount >= course.lessons?.length) {
      setCourseCompleted(true);
      toast.success('🎉 Congratulations! You completed the course!');
    } else if (currentLessonIndex < course.lessons.length - 1) {
      // Move to next lesson
      setCurrentLessonIndex(prev => prev + 1);
    }
  };

  const handleLessonClick = (index) => {
    // Save current progress before switching
    saveProgress(false);
    setCurrentLessonIndex(index);
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={16} />;
      case 'audio': return <Music size={16} />;
      case 'external_url': return <Link size={16} />;
      default: return <FileText size={16} />;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = course.lessons?.length 
    ? Math.round((progress?.completed_lessons?.length || 0) / course.lessons.length * 100) 
    : 0;

  const renderContent = () => {
    if (!currentLesson) return null;

    switch (currentLesson.content_type) {
      case 'video':
        return (
          <div style={{ width: '100%', aspectRatio: '16/9', background: '#000', borderRadius: '12px', overflow: 'hidden' }}>
            <video
              controls
              autoPlay
              style={{ width: '100%', height: '100%' }}
              src={currentLesson.content}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            >
              Your browser does not support video playback.
            </video>
          </div>
        );
      
      case 'audio':
        return (
          <div style={{ padding: '40px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
            <Music size={64} style={{ color: 'var(--color-primary)', marginBottom: '20px' }} />
            <audio
              controls
              autoPlay
              style={{ width: '100%', maxWidth: '400px' }}
              src={currentLesson.content}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            >
              Your browser does not support audio playback.
            </audio>
          </div>
        );
      
      case 'external_url':
        // Check if it's a YouTube URL
        const youtubeMatch = currentLesson.content.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
        if (youtubeMatch) {
          return (
            <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden' }}>
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
                title={currentLesson.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        // Vimeo
        const vimeoMatch = currentLesson.content.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
          return (
            <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden' }}>
              <iframe
                width="100%"
                height="100%"
                src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
                title={currentLesson.title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        // Generic external link
        return (
          <div style={{ padding: '40px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
            <Link size={64} style={{ color: 'var(--color-primary)', marginBottom: '20px' }} />
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>This lesson links to external content</p>
            <a
              href={currentLesson.content}
              target="_blank"
              rel="noopener noreferrer"
              className="gradient-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
            >
              <Play size={16} /> Open External Content
            </a>
          </div>
        );
      
      case 'text':
      default:
        return (
          <div style={{ 
            padding: '32px', 
            background: 'var(--bg-surface)', 
            borderRadius: '12px',
            lineHeight: '1.8',
            fontSize: '15px',
            color: 'var(--text-primary)',
            whiteSpace: 'pre-wrap'
          }}>
            {currentLesson.content}
          </div>
        );
    }
  };

  return (
    <div className="course-viewer" style={{ display: 'flex', gap: '24px', minHeight: 'calc(100vh - 150px)' }}>
      {/* Sidebar - Lesson List */}
      <div style={{ width: '320px', flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            padding: '8px 0',
            marginBottom: '16px',
            fontSize: '14px'
          }}
        >
          <ArrowLeft size={18} /> Back to Courses
        </button>

        <div className="card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: 'var(--text-primary)' }}>{course.title}</h3>
          <span style={{
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            background: course.category === 'wellness' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
            color: course.category === 'wellness' ? 'var(--color-success)' : 'var(--color-secondary)'
          }}>
            {course.category === 'wellness' ? '🧘 Wellness' : '📚 Skill Based'}
          </span>

          {/* Progress Bar */}
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Progress</span>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{progressPercentage}%</span>
            </div>
            <div style={{ height: '8px', background: 'var(--bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ 
                height: '100%', 
                width: `${progressPercentage}%`,
                background: progressPercentage === 100 ? 'var(--color-success)' : 'var(--color-primary)',
                borderRadius: '4px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Lesson List */}
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
              Lessons ({progress?.completed_lessons?.length || 0}/{course.lessons?.length || 0})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {course.lessons?.map((lesson, idx) => (
                <button
                  key={lesson.id}
                  onClick={() => handleLessonClick(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    background: idx === currentLessonIndex ? 'rgba(36, 22, 101, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                >
                  {isLessonCompleted(lesson.id) ? (
                    <CheckCircle size={18} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                  ) : (
                    <Circle size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '13px', 
                      color: idx === currentLessonIndex ? 'var(--color-primary)' : 'var(--text-primary)',
                      fontWeight: idx === currentLessonIndex ? '600' : '400',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {idx + 1}. {lesson.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {getContentIcon(lesson.content_type)}
                      {lesson.duration_minutes} min
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {courseCompleted ? (
          <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
            <Award size={80} style={{ color: 'var(--color-success)', marginBottom: '24px' }} />
            <h2 style={{ margin: '0 0 16px 0', color: 'var(--text-primary)' }}>Course Completed! 🎉</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Congratulations! You've completed "{course.title}". Your learning hours have been automatically added to your daily scan.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={onBack}>
                Browse More Courses
              </button>
            </div>
          </div>
        ) : currentLesson ? (
          <>
            {/* Lesson Header */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '24px', color: 'var(--text-primary)' }}>{currentLesson.title}</h2>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '8px 16px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: isPlaying ? 'var(--color-success)' : 'var(--text-secondary)'
                }}>
                  <Clock size={16} />
                  Time: {formatTime(timeSpent)}
                </div>
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                Lesson {currentLessonIndex + 1} of {course.lessons?.length} • {currentLesson.duration_minutes} min
              </div>
            </div>

            {/* Lesson Content */}
            {renderContent()}

            {/* Actions */}
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={() => currentLessonIndex > 0 && handleLessonClick(currentLessonIndex - 1)}
                className="btn-secondary"
                disabled={currentLessonIndex === 0}
                style={{ opacity: currentLessonIndex === 0 ? 0.5 : 1 }}
              >
                Previous Lesson
              </button>
              
              {isLessonCompleted(currentLesson.id) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-success)' }}>
                  <CheckCircle size={20} /> Completed
                </div>
              ) : (
                <button
                  onClick={handleMarkComplete}
                  className="gradient-btn"
                >
                  <CheckCircle size={16} /> Mark as Complete
                </button>
              )}

              <button
                onClick={() => currentLessonIndex < course.lessons.length - 1 && handleLessonClick(currentLessonIndex + 1)}
                className="gradient-btn"
                disabled={currentLessonIndex === course.lessons?.length - 1}
                style={{ opacity: currentLessonIndex === course.lessons?.length - 1 ? 0.5 : 1 }}
              >
                Next Lesson
              </button>
            </div>
          </>
        ) : (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--text-tertiary)' }}>No lessons available in this course yet.</p>
          </div>
        )}

        {/* Course Benefits */}
        {course.benefits?.length > 0 && !courseCompleted && (
          <div className="card" style={{ padding: '20px', marginTop: '24px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: 'var(--text-primary)' }}>What You'll Learn</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {course.benefits.map((benefit, idx) => (
                <li key={idx} style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseViewer;
